import React, { useState, useEffect, useRef } from 'react';
import './ProductImageGallery.css';

interface Props {
  images: string[];
  alt: string;
  variant?: 'card' | 'detail';
  onImageClick?: () => void;
}

const ProductImageGallery: React.FC<Props> = ({ images, alt, variant = 'card', onImageClick }) => {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const hasMany = images.length > 1;
  const safeIndex = images.length ? Math.min(index, images.length - 1) : 0;
  const current = images[safeIndex] || '';

  useEffect(() => {
    setIndex(0);
  }, [images.join('|')]);

  const go = (dir: -1 | 1) => {
    if (!hasMany) return;
    setIndex(i => (i + dir + images.length) % images.length);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null || !hasMany) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 40) return;
    go(dx < 0 ? 1 : -1);
  };

  if (!current) {
    return (
      <div className={`pig pig--${variant} pig--empty`}>
        <span>📦</span>
      </div>
    );
  }

  return (
    <div
      className={`pig pig--${variant}`}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <img
        key={current}
        src={current}
        alt={hasMany ? `${alt} — image ${safeIndex + 1}` : alt}
        loading="lazy"
        className={onImageClick ? 'pig-img pig-img--clickable' : 'pig-img'}
        onClick={onImageClick}
      />
      {hasMany && (
        <div className="pig-controls" onClick={e => e.stopPropagation()} onTouchEnd={e => e.stopPropagation()}>
          <button type="button" className="pig-nav pig-nav--prev" onClick={() => go(-1)} aria-label="Previous image">‹</button>
          <button type="button" className="pig-nav pig-nav--next" onClick={() => go(1)} aria-label="Next image">›</button>
          <div className="pig-dots">
            {images.map((url, i) => (
              <button
                key={url}
                type="button"
                className={`pig-dot${i === safeIndex ? ' pig-dot--active' : ''}`}
                onClick={() => setIndex(i)}
                aria-label={`Image ${i + 1}`}
              />
            ))}
          </div>
          <span className="pig-count">{safeIndex + 1}/{images.length}</span>
        </div>
      )}
    </div>
  );
};

export default ProductImageGallery;
